import React, { useEffect, useRef, useState } from "react";
import { Badge, Card, Label, Modal, Tooltip } from "flowbite-react";
import { useMutation } from "react-query";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  HiCheckCircle,
  HiEye,
  HiEyeOff,
  HiOutlineClipboardCopy,
  HiOutlineInformationCircle,
  HiOutlineExclamation,
  HiXCircle,
} from "react-icons/hi";

import SubTitle from "../../components/SubTitle";
import Button from "../../components/Button";
import Notification from "../../components/Notification";
import ToastNotification from "../../components/ToastNotification";
import { ToastType } from "../../typesData/ToastNotificationTypes";
import {
  getMemberSnapshot,
  getPasswordRevealRefusal,
  revealMemberPassword,
} from "../../api/memberAccount/actions";
import {
  MemberSnapshot,
  PASSWORD_REVEAL_OPERATOR_UNVERIFIED,
  PASSWORD_REVEAL_RETIRED,
  PasswordRevealRefusal,
  REVEAL_REASON_MAX_LENGTH,
  RevealMemberPasswordResponse,
} from "../../api/memberAccount/types";
import { updateAfyPassword } from "../../api/hubspot";
import { useActorEmail } from "../../hooks/useActorEmail";
import { useOperatorToken } from "../../hooks/useOperatorToken";
import { formatDateTime } from "../../../utils/date";
import {
  memberSecurityStrings as S,
  formatMessage,
} from "./MemberSecurityTab.strings";

/** How long a revealed password stays in the component before it is dropped. */
export const REVEAL_AUTO_CLEAR_MS = 60_000;

interface MemberSecurityTabProps {
  email: string;
  firstName?: string;
  lastName?: string;
  status?: string;
}

type Banner = { type: ToastType; message: string };

const formatCaptureDate = (value: string | null | undefined): string => {
  if (!value) {
    return S.reveal.unknownDate;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? S.reveal.unknownDate
    : formatDateTime(date);
};

const captureSourceLabel = (source: string | null | undefined): string =>
  (source && S.reveal.captureSources[source]) || source || S.none;

/** The message for a typed reveal refusal (ADR-011), never the generic error. */
const refusalMessage = (refusal: PasswordRevealRefusal): string => {
  switch (refusal.code) {
    case "PASSWORD_REVEAL_RATE_LIMITED":
      return refusal.scope === "target"
        ? S.reveal.refusals.rateLimitedTarget
        : S.reveal.refusals.rateLimitedOperator;
    case PASSWORD_REVEAL_RETIRED:
      return S.reveal.retired;
    default:
      return S.reveal.refusals[refusal.code];
  }
};

const SectionCard: React.FC<{
  heading: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ heading, children, testId }) => (
  <Card {...(testId ? { "data-testid": testId } : {})}>
    <div className="pb-3 mb-3 border-b border-gray-100">
      <SubTitle>{heading}</SubTitle>
    </div>
    {children}
  </Card>
);

const MemberSecurityTab: React.FC<MemberSecurityTabProps> = ({
  email,
  firstName,
  lastName,
  status,
}) => {
  const isActive = (status ?? "").toLowerCase() === "active";
  const memberName = `${firstName ?? ""} ${lastName ?? ""}`.trim() || email;

  const actorEmail = useActorEmail();
  // The operator's FusionAuth token proves who reveals (DS task 115). It is
  // read at click time and handed to the reveal request only; only whether one
  // exists is kept for rendering.
  const { hasToken, getOperatorToken, signInAgain } = useOperatorToken();
  const canReveal = Boolean(actorEmail) && hasToken;
  const [snapshot, setSnapshot] = useState<MemberSnapshot | null>(null);
  // The revealed password lives ONLY in this component state: never in the
  // React Query cache, storage, the URL or a log. It is dropped after
  // REVEAL_AUTO_CLEAR_MS, when the member changes and on unmount.
  const [revealed, setRevealed] = useState<RevealMemberPasswordResponse | null>(
    null
  );
  const [isRevealing, setIsRevealing] = useState(false);
  const [refusal, setRefusal] = useState<PasswordRevealRefusal | null>(null);
  const [reason, setReason] = useState("");
  const [showPlain, setShowPlain] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  // Set when the reveal call itself answers 410 PASSWORD_REVEAL_RETIRED (a DS
  // that predates the vault reveal).
  const [revealRefused, setRevealRefused] = useState(false);
  // Bumped on every reveal, member change and unmount so a late answer for a
  // previous member (or an unmounted tab) is discarded.
  const revealRequestRef = useRef(0);
  // Runtime-driven, no build flag: `false` in the snapshot means the DS vault
  // is not configured; an absent field (an older DS) keeps the button.
  const revealUnavailable = snapshot?.passwordRevealAvailable === false;
  const storedCopy = snapshot?.passwordReveal;

  useEffect(() => {
    if (!email) {
      return;
    }
    let active = true;
    setSnapshot(null);
    getMemberSnapshot(email)
      .then((data) => {
        if (active) setSnapshot(data);
      })
      .catch(() => {
        if (active) setSnapshot(null);
      });
    return () => {
      active = false;
    };
  }, [email]);

  useEffect(() => {
    setRevealed(null);
    setShowPlain(false);
    setRefusal(null);
    setRevealRefused(false);
    setIsRevealing(false);
    setReason("");
    return () => {
      revealRequestRef.current += 1;
    };
  }, [email]);

  useEffect(() => {
    if (!revealed) {
      return;
    }
    const timer = setTimeout(() => {
      setRevealed(null);
      setShowPlain(false);
    }, REVEAL_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [revealed]);

  const handleReveal = async () => {
    if (!canReveal) {
      return;
    }
    revealRequestRef.current += 1;
    const requestId = revealRequestRef.current;
    const isLatest = () => requestId === revealRequestRef.current;
    setIsRevealing(true);
    setRefusal(null);
    setBanner(null);
    try {
      const operatorToken = await getOperatorToken();
      if (!isLatest()) return;
      const data = await revealMemberPassword(email, {
        operatorEmail: actorEmail,
        operatorToken,
        reason,
        surface: "security-tab",
      });
      if (!isLatest()) return;
      setRevealed(data);
      setShowPlain(false);
      setReason("");
    } catch (error: unknown) {
      if (!isLatest()) return;
      const typed = getPasswordRevealRefusal(error);
      if (typed?.code === PASSWORD_REVEAL_RETIRED) {
        setRevealRefused(true);
      } else if (typed) {
        setRefusal(typed);
      } else {
        setBanner({ type: "FAILURE", message: S.reveal.error });
      }
    } finally {
      if (isLatest()) setIsRevealing(false);
    }
  };

  const setMutation = useMutation(
    () =>
      updateAfyPassword({
        email,
        password: newPassword,
        passwordConfirmation: newPassword,
      }),
    {
      onSuccess: () => {
        setConfirmOpen(false);
        setNewPassword("");
        const message = formatMessage(S.set.success, { email });
        setBanner({ type: "SUCCESS", message });
        ToastNotification({ type: "success", message });
      },
      onError: () => {
        setConfirmOpen(false);
        const message = formatMessage(S.set.error, { email });
        setBanner({ type: "FAILURE", message });
        ToastNotification({ type: "failure", message });
      },
    }
  );

  const policy = {
    min: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    numberOrSymbol: /[0-9!@#$%^&*()\-_]/.test(newPassword),
  };
  const isPasswordValid = Object.values(policy).every(Boolean);
  const isBusy = isRevealing || setMutation.isLoading;

  const policyRow = (ok: boolean, label: string) => (
    <div className="flex items-center gap-2">
      {ok ? (
        <HiCheckCircle className="h-4 w-4 shrink-0 text-success-500" />
      ) : (
        <HiXCircle className="h-4 w-4 shrink-0 text-gray-400" />
      )}
      <span className={`text-sm ${ok ? "text-neutral-700" : "text-gray-500"}`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="member-security-tab">
      {!isActive && <Notification type="OTHER" message={S.inactive} />}
      {banner && <Notification type={banner.type} message={banner.message} />}

      <SectionCard heading={S.reveal.heading}>
        {revealRefused ? (
          <div
            className="flex items-start gap-2"
            data-testid="password-reveal-retired"
          >
            <HiOutlineInformationCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
            <p className="text-sm text-gray-700">{S.reveal.retired}</p>
          </div>
        ) : revealUnavailable ? (
          <div
            className="flex items-start gap-2"
            data-testid="password-reveal-unavailable"
          >
            <HiOutlineInformationCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
            <p className="text-sm text-gray-700">{S.reveal.unavailable}</p>
          </div>
        ) : revealed ? (
          // Masked in Datadog Session Replay, and a fixed RUM action name so a
          // click never names an action after the password text.
          <div
            className="flex flex-col gap-2"
            data-dd-privacy="mask"
            data-dd-action-name="Revealed password"
          >
            <div className="flex items-center gap-3">
              <span
                className="font-mono text-sm font-semibold text-neutral-800"
                data-testid="revealed-password"
              >
                {showPlain ? revealed.password || S.none : "••••••••••"}
              </span>
              <button
                type="button"
                aria-label={showPlain ? S.reveal.hide : S.reveal.show}
                className="text-gray-500 hover:text-neutral-800"
                onClick={() => setShowPlain((v) => !v)}
              >
                {showPlain ? (
                  <HiEyeOff className="h-4 w-4" />
                ) : (
                  <HiEye className="h-4 w-4" />
                )}
              </button>
              {revealed.password && (
                <Tooltip content={S.reveal.copyTooltip}>
                  <CopyToClipboard
                    text={revealed.password}
                    onCopy={() =>
                      ToastNotification({
                        type: "success",
                        message: S.reveal.copied,
                      })
                    }
                  >
                    <button
                      type="button"
                      aria-label={S.reveal.copyTooltip}
                      className="text-gray-500 hover:text-neutral-800 cursor-pointer"
                    >
                      <HiOutlineClipboardCopy className="h-4 w-4" />
                    </button>
                  </CopyToClipboard>
                </Tooltip>
              )}
            </div>
            {revealed.current === false && (
              <div
                className="flex items-start gap-2 rounded-md bg-yellow-50 p-2"
                role="alert"
                data-testid="password-reveal-stale-caveat"
              >
                <HiOutlineExclamation className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  {formatMessage(S.reveal.staleCaveat, {
                    date: formatCaptureDate(revealed.capturedAt),
                  })}
                </p>
              </div>
            )}
            {revealed.source === "vault" && (
              <p
                className="text-xs text-gray-500"
                data-testid="password-reveal-captured"
              >
                {formatMessage(S.reveal.capturedFrom, {
                  date: formatCaptureDate(revealed.capturedAt),
                  source: captureSourceLabel(revealed.captureSource),
                })}
              </p>
            )}
            {revealed.isLikelyAutoGenerated && (
              <Badge
                color="warning"
                icon={HiOutlineExclamation}
                size="sm"
                className="w-fit"
              >
                {S.reveal.autoGeneratedNote}
              </Badge>
            )}
            <p className="text-xs text-gray-400">{S.reveal.autoHideNote}</p>
          </div>
        ) : (
          <div className="flex max-w-md flex-col gap-3">
            {storedCopy && !storedCopy.stored && (
              <p
                className="text-sm text-gray-600"
                data-testid="password-reveal-not-stored"
              >
                {S.reveal.notStoredYet}
              </p>
            )}
            {storedCopy?.stored && (
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="password-reveal-stored"
              >
                <p className="text-sm text-gray-600">
                  {formatMessage(S.reveal.storedCapturedOn, {
                    date: formatCaptureDate(storedCopy.capturedAt),
                  })}
                </p>
                {storedCopy.current === false && (
                  <Badge
                    color="warning"
                    icon={HiOutlineExclamation}
                    size="sm"
                    className="w-fit"
                    data-testid="password-reveal-stale-badge"
                  >
                    {S.reveal.staleBadge}
                  </Badge>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="member-reveal-reason" value={S.reveal.reasonLabel} />
              <input
                id="member-reveal-reason"
                data-testid="member-reveal-reason"
                type="text"
                autoComplete="off"
                maxLength={REVEAL_REASON_MAX_LENGTH}
                className="mt-1 w-full focus:outline-none px-4 py-[10px] bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-zinc-400"
                placeholder={S.reveal.reasonPlaceholder}
                value={reason}
                disabled={!isActive || isBusy || !canReveal}
                onChange={(event) =>
                  setReason(
                    event.target.value.slice(0, REVEAL_REASON_MAX_LENGTH)
                  )
                }
              />
              <p className="mt-1 text-right text-xs text-gray-400">
                {formatMessage(S.reveal.reasonCounter, {
                  count: String(reason.length),
                  max: String(REVEAL_REASON_MAX_LENGTH),
                })}
              </p>
            </div>
            <div>
              <Button
                variant="LIGHT"
                disabled={!isActive || isBusy || !canReveal}
                isProcessing={isRevealing}
                onClick={handleReveal}
              >
                {S.reveal.button}
              </Button>
            </div>
            {isActive && !actorEmail && (
              <p
                className="text-xs text-gray-500"
                data-testid="password-reveal-no-operator"
              >
                {S.reveal.noOperator}
              </p>
            )}
            {isActive && Boolean(actorEmail) && !hasToken && (
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="password-reveal-no-operator-token"
              >
                <p className="text-xs text-gray-500">
                  {S.reveal.noOperatorToken}
                </p>
                <Button variant="LIGHT" onClick={signInAgain}>
                  {S.reveal.signInAgain}
                </Button>
              </div>
            )}
            {refusal && (
              <div
                className="flex items-start gap-2 rounded-md bg-red-50 p-2"
                role="alert"
                data-testid="password-reveal-refusal"
                data-code={refusal.code}
              >
                <HiOutlineExclamation className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm text-red-700">
                    {refusalMessage(refusal)}
                  </p>
                  {refusal.code === PASSWORD_REVEAL_OPERATOR_UNVERIFIED && (
                    <Button variant="LIGHT" onClick={signInAgain}>
                      {S.reveal.signInAgain}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard heading={S.set.heading}>
        <div className="max-w-md">
          <Label htmlFor="member-new-password" value={S.set.inputLabel} />
          <input
            id="member-new-password"
            data-testid="member-new-password"
            data-dd-privacy="mask"
            type="text"
            autoComplete="off"
            className="mt-1 w-full focus:outline-none px-4 py-[10px] bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-zinc-400"
            placeholder={S.set.placeholder}
            value={newPassword}
            disabled={!isActive || isBusy}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
            {policyRow(policy.min, S.set.policy.min)}
            {policyRow(policy.upper, S.set.policy.upper)}
            {policyRow(policy.lower, S.set.policy.lower)}
            {policyRow(policy.numberOrSymbol, S.set.policy.numberOrSymbol)}
          </div>
          <div className="mt-5">
            <Button
              variant="PRIMARY"
              disabled={!isActive || !isPasswordValid || isBusy}
              onClick={() => setConfirmOpen(true)}
            >
              {S.set.submit}
            </Button>
            {isActive && !isPasswordValid && (
              <p className="mt-2 text-xs text-gray-500">{S.set.disabledHint}</p>
            )}
          </div>
        </div>
      </SectionCard>

      <Modal show={confirmOpen} size="md" onClose={() => setConfirmOpen(false)}>
        <Modal.Header>{S.set.confirmTitle}</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-700">
            {formatMessage(S.set.confirmBody, { name: memberName, email })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="PRIMARY"
            isProcessing={setMutation.isLoading}
            disabled={setMutation.isLoading}
            onClick={() => setMutation.mutate()}
          >
            {S.set.confirmYes}
          </Button>
          <Button
            variant="LIGHT"
            disabled={setMutation.isLoading}
            onClick={() => setConfirmOpen(false)}
          >
            {S.set.confirmNo}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MemberSecurityTab;
